import { ReactElement } from 'react';
import {
  abbreviateScenarioValue,
  SCENARIO_NEGATIVE_PILL_COLOR
} from '@Components/ScenarioPlanningChart/scenario-planning-chart-utils';
import { PercentChange } from '@Components/PercentChange';
import {
  abbreviateValue,
  calcPct,
  MIN_CHANGE_THRESHOLD,
  toPrettyPercent,
  type CellTooltipData
} from '@Pages/GeoMMM/ScenarioPlanning/Modals/ChangeImpact/ChangeImpactUtils';
import { Size, StretchMode } from '@Components/designConstants';

interface CellDetailTooltipProps {
  data: CellTooltipData;
}

export const CellDetailTooltip = ({ data }: CellDetailTooltipProps): ReactElement => {
  const { title, baseline, prevValue, newValue, prevVsBaselinePct, newVsBaselinePct } = data;
  const changeDelta = newValue - prevValue;
  const changePct = Math.abs(changeDelta) < MIN_CHANGE_THRESHOLD ? 0 : calcPct(newValue, prevValue);

  return (
    <div className='cell-detail-tooltip'>
      <div className='tooltip-title'>{title}</div>
      <div className='tooltip-body'>
        <div className='tooltip-row'>
          <span className='tooltip-label'>Baseline</span>
          <div className='tooltip-right'>
            <div className='tooltip-value-col'>
              <span className='tooltip-value'>
                {abbreviateScenarioValue(baseline, { monetary: true })}
              </span>
            </div>
            <div className='tooltip-pill-col' />
          </div>
        </div>
        <div className='tooltip-row'>
          <span className='tooltip-label'>Previous Scenario</span>
          <div className='tooltip-right'>
            <div className='tooltip-value-col'>
              <span className='tooltip-value'>
                {abbreviateScenarioValue(prevValue, { monetary: true })}
              </span>
            </div>
            <div className='tooltip-pill-col'>
              <PercentChange
                stretchMode={StretchMode.FIT}
                negativeColor={SCENARIO_NEGATIVE_PILL_COLOR}
                percentChange={prevVsBaselinePct}
                size={Size.SM}
              />
            </div>
          </div>
        </div>
        <div className='tooltip-row tooltip-row-new'>
          <span className='tooltip-label'>New Scenario</span>
          <div className='tooltip-right'>
            <div className='tooltip-value-col'>
              <span className='tooltip-value'>
                {abbreviateScenarioValue(newValue, { monetary: true })}
              </span>
            </div>
            <div className='tooltip-pill-col'>
              <PercentChange
                stretchMode={StretchMode.FIT}
                negativeColor={SCENARIO_NEGATIVE_PILL_COLOR}
                percentChange={newVsBaselinePct}
                size={Size.SM}
              />
            </div>
          </div>
        </div>
        <div className='tooltip-divider' />
        <div className='tooltip-row tooltip-change-row'>
          <span className='tooltip-label'>Change</span>
          <div className='tooltip-right'>
            <div className='tooltip-value-col'>
              <span className='tooltip-value'>{abbreviateValue(changeDelta)}</span>
            </div>
            <div className='tooltip-pill-col'>
              <span className='tooltip-change-pct'>{toPrettyPercent(changePct)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
